import { useState, useEffect, useMemo } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { Menu, X, ArrowUpRight, User, Search, ShoppingCart } from "lucide-react";
import { NAV, FORMATIONS, POSTS, RESOURCES } from "../data/content";
import Wordmark from "./Wordmark";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATIC_INDEX = [
  ...FORMATIONS.map((f) => ({ label: f.title, sub: `Formation · ${f.level}`, to: "/formation", kw: `${f.title} ${f.tag} formation` })),
  ...POSTS.map((p) => ({ label: p.title, sub: `Article · ${p.cat}`, to: "/blog", kw: `${p.title} ${p.cat} ${p.excerpt}` })),
  ...RESOURCES.map((r) => ({ label: r.title, sub: `Ressource · ${r.cat}`, to: "/ressources", kw: `${r.title} ${r.cat} ${r.desc}` })),
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { count } = useCart();
  const [toolIndex, setToolIndex] = useState([]);

  useEffect(() => {
    axios.get(`${API}/tools`).then(({ data }) =>
      setToolIndex((data || []).map((t) => ({ label: t.name, sub: `Outil · ${t.category}`, to: `/outils-apps/${t.slug}`, kw: `${t.name} ${t.category} ${t.short || ""}` })))
    ).catch(() => {});
  }, []);

  const results = useMemo(() => {
    if (q.trim().length < 2) return [];
    const s = q.toLowerCase();
    return [...toolIndex, ...STATIC_INDEX].filter((i) => i.kw.toLowerCase().includes(s)).slice(0, 8);
  }, [q, toolIndex]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setSearchOpen(false); setQ(""); }, [location.pathname]);

  const goSearch = (to) => { setSearchOpen(false); setQ(""); navigate(to); };

  const light = !scrolled;

  return (
    <>
      <motion.header
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled ? "bg-paper/85 backdrop-blur-xl border-b hairline" : "bg-transparent"
        }`}
        data-testid="navbar"
      >
        <nav className="mx-auto max-w-[1400px] px-5 md:px-10 h-20 flex items-center justify-between">
          <Wordmark dark={light} />

          <div className="hidden xl:flex items-center gap-6">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s|à|&/g, "")}`}
                className={({ isActive }) =>
                  `label-mono link-underline transition-colors whitespace-nowrap ${
                    isActive ? "text-gold" : light ? "text-paper/80 hover:text-paper" : "text-ink/70 hover:text-ink"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <button onClick={() => setSearchOpen(true)} data-testid="nav-search" aria-label="Rechercher"
              className={`p-2 rounded-full transition-colors ${light ? "text-paper hover:bg-white/10" : "text-ink hover:bg-ink/10"}`}>
              <Search size={19} />
            </button>
            <Link to="/panier" data-testid="nav-cart" aria-label="Panier"
              className={`relative p-2 rounded-full transition-colors ${light ? "text-paper hover:bg-white/10" : "text-ink hover:bg-ink/10"}`}>
              <ShoppingCart size={19} />
              {count > 0 && (
                <span data-testid="nav-cart-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-gold text-ink text-[10px] font-bold flex items-center justify-center px-1">{count}</span>
              )}
            </Link>
            {user ? (
              <Link
                to="/mon-espace"
                data-testid="nav-mon-espace"
                className={`hidden md:inline-flex items-center gap-2 rounded-full px-5 py-2.5 label-mono transition-colors duration-300 ${
                  light ? "bg-gold text-ink hover:bg-paper" : "bg-ink text-paper hover:bg-gold hover:text-ink"
                }`}
              >
                <User size={14} /> Mon espace
              </Link>
            ) : (
              <Link
                to="/connexion"
                data-testid="nav-login"
                className={`hidden md:inline-flex items-center gap-2 rounded-full px-5 py-2.5 label-mono transition-colors duration-300 ${
                  light ? "bg-gold text-ink hover:bg-paper" : "bg-ink text-paper hover:bg-gold hover:text-ink"
                }`}
              >
                <User size={14} /> Se connecter
              </Link>
            )}
            <button
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-open"
              className={`xl:hidden p-2 ${light ? "text-paper" : "text-ink"}`}
              aria-label="Ouvrir le menu"
            >
              <Menu size={26} />
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {searchOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[65] bg-ink-950/85 backdrop-blur-sm flex items-start justify-center p-4 pt-24 md:pt-32"
            onClick={() => setSearchOpen(false)}
            data-testid="search-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-xl bg-paper rounded-md overflow-hidden"
            >
              <div className="flex items-center gap-3 px-5 border-b hairline">
                <Search size={20} className="text-ink/40" />
                <input
                  autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                  data-testid="search-input" placeholder="Rechercher un outil, une formation, un article…"
                  className="flex-1 bg-transparent py-5 outline-none text-ink placeholder:text-ink/40"
                />
                <button onClick={() => setSearchOpen(false)} className="text-ink/40 hover:text-ink"><X size={20} /></button>
              </div>
              <div className="max-h-[50vh] overflow-y-auto">
                {q.trim().length < 2 ? (
                  <p className="px-5 py-8 text-ink/40 label-mono">Tapez au moins 2 caractères…</p>
                ) : results.length === 0 ? (
                  <p className="px-5 py-8 text-ink/50" data-testid="search-empty">Aucun résultat pour « {q} ».</p>
                ) : (
                  results.map((r, i) => (
                    <button key={i} onClick={() => goSearch(r.to)} data-testid="search-result"
                      className="w-full text-left px-5 py-4 border-b hairline last:border-0 hover:bg-white transition-colors flex items-center justify-between group">
                      <div>
                        <div className="font-display text-lg font-semibold text-ink">{r.label}</div>
                        <div className="label-mono text-ink/45 mt-0.5">{r.sub}</div>
                      </div>
                      <ArrowUpRight size={16} className="text-ink/30 group-hover:text-gold group-hover:rotate-45 transition-all" />
                    </button>
                  ))
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-ink text-paper grain"
            data-testid="mobile-menu"
          >
            <div className="relative z-10 flex flex-col h-full p-6">
              <div className="flex items-center justify-between">
                <Wordmark dark />
                <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" className="p-2" aria-label="Fermer le menu">
                  <X size={28} />
                </button>
              </div>
              <div className="flex-1 flex flex-col justify-center gap-0.5 overflow-y-auto py-4">
                {NAV.map((n, i) => (
                  <motion.div
                    key={n.to}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * i }}
                  >
                    <Link to={n.to} className="font-display text-3xl font-semibold py-1 block hover:text-gold transition-colors">
                      {n.label}
                    </Link>
                  </motion.div>
                ))}
                <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * NAV.length }}>
                  <Link to={user ? "/mon-espace" : "/connexion"} className="font-display text-3xl font-semibold py-1 block text-gold">
                    {user ? "Mon espace" : "Se connecter"}
                  </Link>
                </motion.div>
              </div>
              <p className="label-mono text-steel">Nova Lean Consulting — Lean Today, Better Tomorrow</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
